// camera — one photo, from wherever the person has one.
//
// Native goes through Capacitor's Camera plugin; the web goes through a plain
// file input with `capture`, which is what mobile Safari and Chrome turn into a
// camera. Both return the same thing, so calling code never branches.
//
// There is nothing to configure. Unlike auth and paywall, this addon is useful
// the moment it is enabled — the setup steps are about the native projects
// picking up the plugin, not about making the feature work.

import { plugin, isNative } from '../../src/core/native.js';
import { toast } from '../../src/core/util.js';

/** @typedef {{ dataUrl: string, format: string }} Photo */

async function fromPlugin(source) {
  const Camera = plugin('Camera');
  if (!Camera?.getPhoto) return null;
  try {
    const photo = await Camera.getPhoto({
      quality: 80,
      allowEditing: false,
      resultType: 'dataUrl',
      source,
      // Asking every time is the honest default. A silent permission prompt on
      // first launch reads as the app grabbing the camera for no reason.
      promptLabelHeader: 'Add a photo',
    });
    return { dataUrl: photo.dataUrl, format: photo.format ?? 'jpeg' };
  } catch (e) {
    // A cancel is not an error. Capacitor reports it as one.
    if (/cancel/i.test(String(e?.message ?? e))) return null;
    throw e;
  }
}

function fromFileInput({ camera }) {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    if (camera) input.capture = 'environment';
    input.style.display = 'none';
    // Safari fires nothing when the sheet is dismissed, so the element is
    // removed on the next interaction rather than on a cancel event.
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      input.remove();
      if (!file) return resolve(null);
      const reader = new FileReader();
      reader.onload = () => resolve({
        dataUrl: String(reader.result),
        format: (file.type.split('/')[1] || 'jpeg'),
      });
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
    document.body.appendChild(input);
    input.click();
  });
}

/**
 * Take a photo with the camera.
 * @returns {Promise<Photo|null>} null when the person backed out.
 */
export async function takePhoto() {
  if (isNative) {
    const shot = await fromPlugin('CAMERA');
    if (shot !== undefined) return shot;
  }
  return fromFileInput({ camera: true });
}

/**
 * Choose an existing photo.
 * @returns {Promise<Photo|null>} null when the person backed out.
 */
export async function choosePhoto() {
  if (isNative) {
    const shot = await fromPlugin('PHOTOS');
    if (shot !== undefined) return shot;
  }
  return fromFileInput({ camera: false });
}

/** Camera on device, library on the web — whichever is least surprising here. */
export async function addPhoto() {
  try {
    return isNative ? await takePhoto() : await fromFileInput({ camera: true });
  } catch {
    toast('Could not open the camera');
    return null;
  }
}

export default {
  id: 'camera',
  // No boot: there is nothing to initialise, and asking for permission before
  // the person has asked for the feature is the fastest way to be denied it.
};
